import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StudentPasswordAPIs from "utilities/api/StudentPasswordAPIs";
import BackgroundImage from "../../assets/background.png";

const boxStyle = {
  display: "flex",
  color: "#5C5C5C",
  fontSize: "18px",
  fontWeight: 400,
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.75rem",
  alignSelf: "stretch",
};

const typographyStyle = {
  textAlign: "center",
  fontFamily: "Inter",
  fontSize: "24px",
  fontWeight: 400,
  lineHeight: "normal",
  letterSpacing: "-0.01375rem",
};

// TextField custom styles
const styledTextfield = {
  display: "flex",
  gap: "0.2rem",
  alignSelf: "stretch",
  border: "1px solid #B5B5B5",
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-root": {
    borderRadius: "12px",
    "& input": {
      padding: "0.6rem 1rem",
    },
  },
};

// Password validation schema with security requirements
const validation_schema = Yup.object({
  new_password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    )
    .matches(/^\S*$/, "Password must not contain spaces")
    .required("Required"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("new_password"), null], "Passwords must match")
    .required("Required"),
});

/**
 * ResetPasswordPage Component
 * Handles both first-time login password setup and password reset via email token
 */
const ResetPasswordPage = () => {
  const [passwordReset, setPasswordReset] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract reset token from URL query parameters
  const token = new URLSearchParams(location.search).get("token");
  const navigationState = location.state;

  const formik = useFormik({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema: validation_schema,
    onSubmit: (values) => {
      // Determine if this is first-time login or password reset
      const isFirstLogin = navigationState?.isFirstLogin;

      // Prepare API payload based on scenario
      const data = isFirstLogin
        ? {
            new_password: values.new_password,
            user_id: navigationState.user_id,
          }
        : {
            token: token,
            new_password: values.new_password,
          };

      // Handle first-time login password setup
      if (isFirstLogin) {
        StudentPasswordAPIs.FirstTimeLogin(data)
          .then((res) => {
            if (res && (res.message || res.data)) {
              setPasswordReset(true);
              toast.success(
                "Password set successfully! You can now login with your new password."
              );
            }
          })
          .catch((error) => {
            console.error("First time login error:", error);
            toast.error(
              "Failed to set password. Please try again or contact support."
            );
          });
      } else {
        // Handle password reset via email token
        StudentPasswordAPIs.ResetPassword(data)
          .then((res) => {
            if (res.status === 200 || res) {
              setPasswordReset(true);
              toast.success("Password reset successfully!");
            }
          })
          .catch((error) => {
            console.error("Password reset error:", error);
            if (
              error.response?.status === 400 ||
              error.response?.status === 401
            ) {
              toast.error(
                "Invalid or expired reset token. Please request a new password reset."
              );
              setTimeout(() => navigate("/forgot_password"), 2000);
            } else {
              toast.error("Failed to reset password. Please try again.");
            }
          });
      }
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        minHeight: "100vh",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          boxSizing: "border-box",
          backgroundImage: `url(${BackgroundImage})`,
          backgroundSize: "cover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {!passwordReset ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.5rem",
              padding: "1rem",
              borderRadius: "1rem",
              backgroundColor: "#FFF",
              width: { xs: "90%", sm: "80%", md: "40%", lg: "25.2rem" },
              boxShadow: "4px 4px 44px 0px #0000001A",
              marginTop: { xs: "5rem", md: "8rem" },
            }}
          >
            <Typography
              sx={{
                ...typographyStyle,
                fontSize: "28px",
                fontWeight: 600,
                mb: -2,
              }}
            >
              Reset Your Password
            </Typography>
            <Typography
              sx={{
                color: "#5C5C5C",
                fontSize: "18px",
                textAlign: "center",
                fontWeight: 400,
              }}
            >
              Please enter your new password below.
            </Typography>
            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.5rem",
                width: "100%",
              }}
            >
              <Box sx={boxStyle}>
                <Typography sx={typographyStyle}>New Password*</Typography>
                <TextField
                  id="new_password"
                  name="new_password"
                  type="password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.new_password &&
                    Boolean(formik.errors.new_password)
                  }
                  sx={styledTextfield}
                  placeholder="Enter new password"
                  fullWidth
                />
                {formik.touched.new_password && formik.errors.new_password && (
                  <Typography color="error" variant="body2">
                    {formik.errors.new_password}
                  </Typography>
                )}
              </Box>
              <Box sx={boxStyle}>
                <Typography sx={typographyStyle}>Confirm Password*</Typography>
                <TextField
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={formik.values.confirm_password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.confirm_password &&
                    Boolean(formik.errors.confirm_password)
                  }
                  sx={styledTextfield}
                  placeholder="Confirm new password"
                  fullWidth
                />
                {formik.touched.confirm_password &&
                  formik.errors.confirm_password && (
                    <Typography color="error" variant="body2">
                      {formik.errors.confirm_password}
                    </Typography>
                  )}
              </Box>
              <Button
                type="submit"
                sx={{
                  display: "flex",
                  padding: "0.8rem 1rem",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  alignSelf: "stretch",
                  borderRadius: "0.75rem",
                  backgroundColor: "#B2D7FF",
                  color: "#212121",
                  transition: "background-color 0.3s",
                  "&:hover": {
                    backgroundColor: "#1f62c9",
                  },
                  "&:active": {
                    backgroundColor: "#8DAFDD",
                  },
                }}
              >
                <Typography
                  sx={{
                    textAlign: "center",
                    fontFamily: "Inter",
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    lineHeight: "normal",
                    letterSpacing: "-0.01375rem",
                    textTransform: "none",
                  }}
                >
                  Reset Password
                </Typography>
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.5rem",
              padding: "2rem",
              borderRadius: "1rem",
              backgroundColor: "#FFF",
              width: { xs: "90%", sm: "80%", md: "40%", lg: "25.2rem" },
              boxShadow: "4px 4px 44px 0px #0000001A",
              marginTop: { xs: "5rem", md: "8rem" },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 50, color: "#1e6eb2" }} />
            <Typography
              sx={{ ...typographyStyle, fontSize: "28px", fontWeight: 600 }}
            >
              Password Reset Successfully
            </Typography>
            <Typography
              sx={{ color: "#5C5C5C", fontSize: "16px", textAlign: "center" }}
            >
              Your password has been reset successfully. You can now log in with
              your new password.
            </Typography>
            <Button
              onClick={() => navigate("/login")}
              sx={{
                display: "flex",
                padding: "1rem",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                alignSelf: "stretch",
                borderRadius: "0.75rem",
                backgroundColor: "#FFF",
                border: "1px solid #007BFF",
                "&:hover": {
                  backgroundColor: "#B2D7FF",
                },
                textTransform: "none",
              }}
            >
              <Typography
                sx={{
                  color: "#007BFF",
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01375rem",
                }}
              >
                Go to Login
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
